import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../Dashboard/NavBar";
import DashboardHeader from "../Dashboard/Header";
import Breadcrumb from "../Common/Breadcrumb";
import Avatar from "../../Assets/avatar.png";
import { auth } from "../../firebase";
import { getQuizzesByCategory, getCategoryById, getCategoryBySlug, deleteQuiz, updateQuiz } from "../../services/firestoreService";
import { useNotifications } from "../../contexts/NotificationContext";
import { Trash2, Edit, Lock, Globe, Search, Share2, Plus, Check } from "lucide-react";

function CategoryDetail() {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [showDropdown, setShowDropdown] = useState(false);
  const [category, setCategory] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteQuizModal, setShowDeleteQuizModal] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [selectedQuizTitle, setSelectedQuizTitle] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null); // Reset error state
        const userId = auth.currentUser?.uid || null;

        console.log('Fetching category with slug:', categorySlug);

        // Try to get category by slug first, fallback to ID if not found
        let categoryResult = await getCategoryBySlug(categorySlug);
        console.log('Category by slug result:', categoryResult);

        // If slug lookup fails, try as ID (for backward compatibility)
        if (!categoryResult.success) {
          console.log('Slug lookup failed, trying as ID');
          categoryResult = await getCategoryById(categorySlug);
          console.log('Category by ID result:', categoryResult);
        }

        if (!categoryResult.success) {
          console.error('Category not found:', categoryResult.error);
          setError("Category not found");
          setLoading(false);
          return;
        }

        setCategory(categoryResult.data);

        // Fetch quizzes using the category ID
        const quizzesResult = await getQuizzesByCategory(categoryResult.data.id, userId);

        if (quizzesResult.success) {
          setQuizzes(quizzesResult.data);
        } else {
          console.error('Failed to fetch quizzes:', quizzesResult.error);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load category data");
        setLoading(false);
      }
    };

    fetchData();
  }, [categorySlug]);

  const handleCreateQuiz = () => {
    navigate(`/makequiz?categoryId=${category.id}`);
  };

  const handleDeleteQuizClick = (quiz) => {
    setSelectedQuizId(quiz.id);
    setSelectedQuizTitle(quiz.title);
    setShowDeleteQuizModal(true);
  };

  const handleDeleteQuiz = async () => {
    if (!selectedQuizId) return;

    setDeleting(true);
    try {
      const result = await deleteQuiz(selectedQuizId);
      if (result.success) {
        setQuizzes(prev => prev.filter(q => q.id !== selectedQuizId));
        notify({
          title: 'Quiz Deleted',
          message: 'Quiz has been deleted successfully',
          type: 'success',
        });
      } else {
        notify({
          title: 'Error',
          message: result.error || 'Failed to delete quiz',
          type: 'error',
        });
      }
    } catch (err) {
      notify({
        title: 'Error',
        message: 'An unexpected error occurred',
        type: 'error',
      });
    } finally {
      setDeleting(false);
      setShowDeleteQuizModal(false);
      setSelectedQuizId(null);
      setSelectedQuizTitle("");
    }
  };

  const handleToggleVisibility = async (quiz) => {
    try {
      const currentIsPublic = quiz.settings?.isPublic || false;
      const result = await updateQuiz(quiz.id, {
        settings: {
          ...quiz.settings,
          isPublic: !currentIsPublic
        }
      });

      if (result.success) {
        // Update local state
        setQuizzes(prev => prev.map(q =>
          q.id === quiz.id ? {
            ...q,
            settings: { ...q.settings, isPublic: !currentIsPublic }
          } : q
        ));

        notify({
          title: 'Privacy Updated',
          message: `Quiz "${quiz.title}" is now ${!currentIsPublic ? 'Public' : 'Private'}`,
          type: 'success',
        });
      }
    } catch (err) {
      notify({
        title: 'Error',
        message: 'Failed to update privacy settings',
        type: 'error',
      });
    }
  };

  const handleCopyLink = (quiz) => {
    const link = `${window.location.origin}/quiz/${quiz.id}`;
    navigator.clipboard.writeText(link);
    // No notification to prevent potential DDoS from repeated copying
  };

  // Filter quizzes based on search query
  const filteredQuizzes = quizzes.filter((quiz) =>
    quiz.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-100">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-100">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || "Category not found"}</p>
            <button
              onClick={() => navigate("/main")}
              className="bg-gray-700 text-white px-6 py-2 rounded-lg hover:bg-gray-800"
            >
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      {/* Side Navigation Bar */}
      <Navbar />

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Dashboard Header */}
        <div className="flex-shrink-0 py-4 md:py-6 px-4 md:px-8 bg-gray-100">
          <DashboardHeader
            toggleDropdown={toggleDropdown}
            showDropdown={showDropdown}
            Avatar={Avatar}
          />
        </div>

        {/* Content */}
        <div className="flex-1 px-4 md:px-8 pb-4 md:pb-8 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Header Section */}
            <div className="px-6 pt-6 pb-6">
              {/* Breadcrumb */}
              {category && <Breadcrumb categoryId={category.id} />}

              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                {/* Title and Count */}
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                    {category.name}
                  </h1>
                  <p className="text-gray-500 text-base">
                    {quizzes.length} {quizzes.length === 1 ? 'Quiz' : 'Quizzes'} Available
                  </p>
                </div>

                {/* Search Bar - Right Aligned */}
                {quizzes.length > 0 && (
                  <div className="relative md:w-80 md:mt-2">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search quizzes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 transition-all"
                      style={{
                        border: '2px solid #D1D5DB'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Quizzes Grid */}
            {filteredQuizzes.length === 0 && searchQuery ? (
              <div className="text-center py-16 px-6">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No matches found
                </h3>
                <p className="text-gray-500 text-sm">
                  Try adjusting your search to find what you're looking for.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 px-6 pb-6">
                {/* Create Quiz Card - First position in grid */}
                <button
                  onClick={handleCreateQuiz}
                  className="relative rounded-lg overflow-hidden cursor-pointer select-none transition-all duration-200 border-2 border-dashed bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center gap-2 group"
                  style={{
                    aspectRatio: '1/1',
                    borderColor: '#D1D5DB'
                  }}
                >
                  <div className="transition-transform group-hover:scale-110">
                    <Plus className="w-10 h-10 text-gray-600" strokeWidth={2} />
                  </div>
                  <span className="text-gray-700 font-medium text-sm">Create Quiz</span>
                </button>

                {/* Existing Quizzes - Show max 7 */}
                {filteredQuizzes.slice(0, 7).map((quiz) => (
                  <QuizCard
                    key={quiz.id}
                    quiz={quiz}
                    onDelete={() => handleDeleteQuizClick(quiz)}
                    onToggleVisibility={() => handleToggleVisibility(quiz)}
                    onCopyLink={() => handleCopyLink(quiz)}
                    onView={() => navigate(`/quiz/${quiz.id}`)}
                    onEdit={() => navigate(`/makequiz?quizId=${quiz.id}`)}
                    onTakeQuiz={() => navigate(`/quiz/${quiz.id}/take`)}
                  />
                ))}

                {/* View All Card - Show if more than 7 quizzes */}
                {filteredQuizzes.length > 7 && !searchQuery && (
                  <button
                    onClick={() => navigate(`/category/${category.slug || category.id}/all-quizzes`)}
                    className="relative rounded-lg overflow-hidden cursor-pointer select-none transition-all duration-200 border-2 bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 flex flex-col items-center justify-center gap-2 group"
                    style={{
                      aspectRatio: '1/1',
                      borderColor: '#93C5FD'
                    }}
                  >
                    <div className="transition-transform group-hover:scale-110">
                      <Search className="w-10 h-10 text-blue-600" strokeWidth={2} />
                    </div>
                    <div className="text-center px-2">
                      <p className="text-blue-700 font-semibold text-sm">View All</p>
                      <p className="text-blue-600 text-xs mt-1">
                        +{filteredQuizzes.length - 7} more
                      </p>
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Quiz Modal */}
      {showDeleteQuizModal && (
        <SafeDeleteQuizModal
          quizTitle={selectedQuizTitle}
          onConfirm={handleDeleteQuiz}
          onCancel={() => {
            setShowDeleteQuizModal(false);
            setSelectedQuizId(null);
            setSelectedQuizTitle("");
          }}
          loading={deleting}
        />
      )}

    </div>
  );
}

// Premium Quiz Card with Clean Structural Zones
const QuizCard = ({ quiz, onDelete, onToggleVisibility, onCopyLink, onView, onEdit, onTakeQuiz }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [justCopied, setJustCopied] = useState(false);
  const totalQuestions = quiz.questions?.length || 0;

  // Calculate total points from all questions
  const totalPoints = quiz.questions?.reduce((sum, q) => sum + (q.points || 0), 0) || 0;

  // Get actual time limit from quiz settings (not estimated)
  const timeLimit = quiz.settings?.timeLimit || null;
  const timeDisplay = timeLimit ? `${timeLimit}` : '∞'; // Show actual limit or infinity symbol

  // Check if current user is the creator
  const isCreator = auth.currentUser?.uid === quiz.creatorId;

  // Category-based fallback images
  const categoryFallbacks = {
    technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=500&h=300&fit=crop',
    medicine: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=500&h=300&fit=crop',
    agriculture: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=500&h=300&fit=crop',
    history: 'https://images.unsplash.com/photo-1461360370896-922624d12aa1?w=500&h=300&fit=crop',
    art: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=500&h=300&fit=crop',
    sport: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=500&h=300&fit=crop',
    mythology: 'https://images.unsplash.com/photo-1532153955177-f59af40d6472?w=500&h=300&fit=crop'
  };

  // Gradient fallbacks if image fails
  const gradients = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-green-500 to-teal-500',
    'from-orange-500 to-red-500',
    'from-indigo-500 to-purple-500',
    'from-yellow-500 to-orange-500',
  ];

  const gradientIndex = quiz.title.length % gradients.length;
  const gradient = gradients[gradientIndex];

  // Get fallback image based on category or use quiz image
  const categorySlug = quiz.categorySlug || 'technology';
  const coverImage = quiz.image || categoryFallbacks[categorySlug] || categoryFallbacks.technology;

  return (
    <div
      onClick={(e) => {
        // Navigate to quiz overview page using slug
        const quizSlug = quiz.slug || quiz.title.toLowerCase().replace(/\s+/g, '-');
        navigate(`/quiz/${quizSlug}/overview`);
      }}
      className="relative group bg-white border-2 border-gray-200 rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-1 hover:border-blue-400 cursor-pointer"
      style={{ aspectRatio: '1/1' }}
    >
      {/* ZONE 1: Cover Image with Hover Overlay (50% height) */}
      <div className="relative overflow-hidden bg-gray-200" style={{ height: '50%' }}>
        {!imageError ? (
          <img
            src={coverImage}
            alt={quiz.title}
            onError={() => setImageError(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${gradient}`}></div>
        )}

        {/* Subtle Overlay */}
        <div className="absolute inset-0 bg-black/10"></div>

        {/* Status Badge - Top Left */}
        <div className="absolute top-2 left-2">
          {quiz.settings?.isPublic ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500/90 backdrop-blur-sm text-white text-[10px] font-semibold rounded-full">
              <Globe className="w-3 h-3" />
              Public
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-700/90 backdrop-blur-sm text-white text-[10px] font-semibold rounded-full">
              <Lock className="w-3 h-3" />
              Private
            </span>
          )}
        </div>

        {/* Management Menu - Top Right (Creator Only) */}
        {isCreator && (
          <div className="absolute top-2 right-2 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1.5 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full shadow-md transition-all"
            >
              <svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="5" r="1.5"/>
                <circle cx="12" cy="12" r="1.5"/>
                <circle cx="12" cy="19" r="1.5"/>
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
                <div className="absolute top-full right-0 mt-1 w-44 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-30">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onEdit(); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Quiz
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onToggleVisibility(); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
                  >
                    {quiz.settings?.isPublic ? <><Lock className="w-4 h-4" />Make Private</> : <><Globe className="w-4 h-4" />Make Public</>}
                  </button>
                  {quiz.settings?.isPublic && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(false);
                        onCopyLink();
                        setJustCopied(true);
                        setTimeout(() => setJustCopied(false), 2000);
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
                    >
                      {justCopied ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-green-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Share2 className="w-4 h-4" />
                          Copy Link
                        </>
                      )}
                    </button>
                  )}
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDelete(); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Quiz
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Glassmorphic Hover Overlay with Actions */}
        <div className="absolute inset-0 bg-white/10 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3 rounded-t-xl">
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              onMouseEnter={() => setHoveredButton('edit')}
              onMouseLeave={() => setHoveredButton(null)}
              className="p-2 bg-white hover:bg-gray-50 rounded-full shadow-xl transition-all duration-200 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 border-2 border-gray-200"
              style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
            >
              <Edit className="w-4 h-4 text-gray-700" />
            </button>
            {hoveredButton === 'edit' && (
              <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-medium py-1.5 px-2.5 rounded-md whitespace-nowrap z-50 shadow-lg">
                Edit
              </span>
            )}
          </div>

          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (quiz.settings?.isPublic) {
                  onCopyLink();
                  setJustCopied(true);
                  setTimeout(() => setJustCopied(false), 2000);
                }
              }}
              onMouseEnter={() => setHoveredButton('share')}
              onMouseLeave={() => setHoveredButton(null)}
              disabled={!quiz.settings?.isPublic}
              className={`p-2 rounded-full shadow-xl transition-all duration-200 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 delay-75 border-2 ${
                justCopied
                  ? 'bg-green-500 border-green-500'
                  : quiz.settings?.isPublic
                    ? 'bg-white hover:bg-gray-50 border-gray-200'
                    : 'bg-gray-200 border-gray-300 cursor-not-allowed'
              }`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
            >
              {justCopied ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <Share2 className={`w-4 h-4 ${quiz.settings?.isPublic ? 'text-gray-700' : 'text-gray-400'}`} />
              )}
            </button>
            {hoveredButton === 'share' && !justCopied && (
              <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-medium py-1.5 px-2.5 rounded-md whitespace-nowrap z-50 shadow-lg">
                {quiz.settings?.isPublic ? 'Copy Link' : 'Make Public'}
              </span>
            )}
            {justCopied && (
              <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-medium py-1.5 px-2.5 rounded-md whitespace-nowrap z-50 shadow-lg">
                Copied!
              </span>
            )}
          </div>

          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              onMouseEnter={() => setHoveredButton('delete')}
              onMouseLeave={() => setHoveredButton(null)}
              className="p-2 bg-white hover:bg-red-50 rounded-full shadow-xl transition-all duration-200 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 delay-150 border-2 border-red-200"
              style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
            {hoveredButton === 'delete' && (
              <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-medium py-1.5 px-2.5 rounded-md whitespace-nowrap z-50 shadow-lg">
                Delete
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ZONE 2 & 3: Content Area (50% height) */}
      <div className="flex flex-col justify-center items-center p-4 text-center" style={{ height: '50%' }}>
        {/* Quiz Title - More Space */}
        <h3 className="text-lg font-bold text-gray-900 line-clamp-3 leading-tight mb-4 px-2">
          {quiz.title}
        </h3>

        {/* Metrics Row - Centered with Emoji Icons */}
        <div className="flex items-center justify-center gap-4 text-sm text-gray-700">
          <span className="inline-flex items-center gap-1.5">
            <span className="text-base">📄</span>
            <span className="font-semibold">{totalQuestions}</span>
          </span>

          {totalPoints > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-base">⭐</span>
              <span className="font-semibold">{totalPoints}</span>
            </span>
          )}

          <span className="inline-flex items-center gap-1.5">
            <span className="text-base">⏱️</span>
            <span className="font-semibold">{timeDisplay}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

// Simple & Ergonomic Delete Quiz Modal
const SafeDeleteQuizModal = ({ quizTitle, onConfirm, onCancel, loading }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !loading) {
          onCancel();
        }
      }}
    >
      {/* Background overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={loading ? undefined : onCancel}
      ></div>

      {/* Modal panel - Compact & Clean */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-in fade-in zoom-in-95 duration-200">

        {/* Icon & Title */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-4">
            <Trash2 className="w-7 h-7 text-red-600" />
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Delete Quiz?
          </h3>

          <p className="text-sm text-gray-600 leading-relaxed">
            Are you sure you want to delete <span className="font-semibold text-gray-900">"{quizTitle}"</span>?
          </p>
        </div>

        {/* Warning - Simplified */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-red-800 text-center">
            ⚠️ This action cannot be undone. All quiz data will be permanently deleted.
          </p>
        </div>

        {/* Buttons - Clear Hierarchy */}
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-3 text-sm font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Deleting...
              </span>
            ) : (
              "Delete Quiz"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryDetail;
